/**
 * Developer Portal Page
 * Customer-facing API documentation, sandbox provisioning, and onboarding progress.
 * Accessible to logged-in organization admins and authorized users.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { GatedTabContent } from '@/components/common/GatedTabContent';
import {
  Book,
  Code,
  Key,
  Rocket,
  CheckCircle,
  Circle,
  ChevronRight,
  Copy,
  ExternalLink,
  RefreshCw,
  Trash2,
  Shield,
  Zap,
  Globe,
  Database,
  MessageSquare,
  BarChart3,
  Users,
  FileText,
  Webhook,
  Settings,
  ArrowRight,
  AlertCircle,
  Eye,
  EyeOff,
  Loader2,
  Search,
  Server,
} from 'lucide-react';
import { getWeaveinApiUrl } from '@/src/config';
import { useSubtabNavigation } from '@/hooks/useSubtabNavigation';

// ============================================================================
// Types
// ============================================================================

interface DocPage {
  id: number;
  slug: string;
  title: string;
  content: string;
  excerpt?: string;
  category: string;
  subcategory?: string;
  display_order: number;
  tags: string[];
  is_featured: boolean;
  published_at?: string;
  updated_at?: string;
}

interface DocCategory {
  category: string;
  docs: DocPage[];
}

interface WorkflowStep {
  order: number;
  method: string;
  path: string;
  description: string;
  required_fields?: string[];
}

interface CuratedWorkflow {
  title: string;
  description: string;
  steps: WorkflowStep[];
}

interface Sandbox {
  id: number;
  organization_id: number;
  environment_name: string;
  status: string;
  client_id?: string;
  api_key_prefix?: string;
  api_key?: string;
  rate_limit_per_minute: number;
  daily_request_quota: number;
  monthly_request_quota: number;
  allowed_scopes: string[];
  webhook_url?: string;
  provisioned_at?: string;
  activated_at?: string;
  expires_at?: string;
}

interface OnboardingProgress {
  organization_id: number;
  onboarding_status: string;
  steps: Record<string, { completed: boolean; at?: string } | boolean>;
  sandbox?: Sandbox;
}

interface UsageStats {
  sandbox_id: number;
  total_requests: number;
  requests_today: number;
  requests_this_month: number;
  error_count: number;
  avg_response_time_ms?: number;
  top_endpoints: { endpoint: string; method: string; count: number }[];
}

// ============================================================================
// Helper Components
// ============================================================================

const CopyButton: React.FC<{ text: string; label?: string }> = ({ text, label }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button
      onClick={handleCopy}
      className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded transition-colors"
      title={`Copy ${label || 'to clipboard'}`}
    >
      {copied ? <CheckCircle className="w-3 h-3 text-green-500" /> : <Copy className="w-3 h-3" />}
      {copied ? 'Copied!' : (label || 'Copy')}
    </button>
  );
};

const StepIndicator: React.FC<{ completed: boolean; label: string; timestamp?: string; number: number }> = ({
  completed, label, timestamp, number,
}) => (
  <div className="flex items-start gap-3 py-3">
    <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
      completed ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-400'
    }`}>
      {completed ? <CheckCircle className="w-5 h-5" /> : number}
    </div>
    <div>
      <p className={`font-medium ${completed ? 'text-green-700' : 'text-gray-600'}`}>{label}</p>
      {timestamp && <p className="text-xs text-gray-400 mt-0.5">{new Date(timestamp).toLocaleDateString()}</p>}
    </div>
  </div>
);

const CodeBlock: React.FC<{ code: string; language?: string }> = ({ code, language }) => (
  <div className="relative group">
    <pre className="bg-gray-900 text-gray-100 rounded-lg p-4 text-sm overflow-x-auto">
      <code>{code}</code>
    </pre>
    <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
      <CopyButton text={code} />
    </div>
  </div>
);

// ============================================================================
// Main Category Icons
// ============================================================================

const categoryIcons: Record<string, React.ReactNode> = {
  'getting-started': <Rocket className="w-5 h-5" />,
  'api-reference': <Code className="w-5 h-5" />,
  'guides': <Book className="w-5 h-5" />,
  'webhooks': <Webhook className="w-5 h-5" />,
  'changelog': <FileText className="w-5 h-5" />,
};

const methodColors: Record<string, string> = {
  GET: 'bg-emerald-50 text-emerald-700',
  POST: 'bg-blue-50 text-blue-700',
  PUT: 'bg-amber-50 text-amber-700',
  PATCH: 'bg-amber-50 text-amber-700',
  DELETE: 'bg-red-50 text-red-700',
};

const categoryLabels: Record<string, string> = {
  'getting-started': 'Getting Started',
  'api-reference': 'API Reference',
  'guides': 'Guides',
  'webhooks': 'Webhooks',
  'changelog': 'Changelog',
};

// ============================================================================
// Main Developer Portal Page
// ============================================================================

export default function DeveloperPortalPage() {
  const [activeTab, setActiveTab] = useState<'overview' | 'docs' | 'api-guide' | 'sandbox' | 'usage'>('overview');
  useSubtabNavigation(
    (tab) => setActiveTab(tab as typeof activeTab),
    ['overview', 'docs', 'api-guide', 'sandbox', 'usage'] as const,
  );
  const [docCategories, setDocCategories] = useState<DocCategory[]>([]);
  const [selectedDoc, setSelectedDoc] = useState<DocPage | null>(null);
  const [onboarding, setOnboarding] = useState<OnboardingProgress | null>(null);
  const [sandbox, setSandbox] = useState<Sandbox | null>(null);
  // Shown only when this organisation's key actually carries the partner-doula
  // scopes. Offering the collection to an org that cannot call a single one of
  // its endpoints is worse than not offering it: every request 403s and the
  // integrator cannot tell a wrong key from a wrong entitlement.
  // What this ORGANISATION may be granted, from /scopes. Deliberately NOT
  // sandbox.allowed_scopes: that describes a key, and gating a download on a
  // key hides the collection in the two moments it is most wanted — before the
  // first key is provisioned, and after one is deactivated. Both produce
  // sandbox === null, which is how the Partner Doula collection disappeared
  // from the page the moment a test key was retired.
  const [grantableScopes, setGrantableScopes] = useState<string[]>([]);
  const hasDoulaEntitlement =
    grantableScopes.some(s => s.endsWith(':partner-doula')) ||
    !!sandbox?.allowed_scopes?.some(s => s.endsWith(':partner-doula'));
  const [usage, setUsage] = useState<UsageStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [sandboxRequesting, setSandboxRequesting] = useState(false);
  const [deactivating, setDeactivating] = useState(false);
  const [useCase, setUseCase] = useState('');
  const [showApiKey, setShowApiKey] = useState(false);
  const [newApiKey, setNewApiKey] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [workflows, setWorkflows] = useState<CuratedWorkflow[]>([]);
  const [workflowsLoading, setWorkflowsLoading] = useState(false);
  const [workflowsLoaded, setWorkflowsLoaded] = useState(false);

  const apiUrl = getWeaveinApiUrl();

  const getHeaders = useCallback(() => {
    const token = localStorage.getItem('auth_token');
    const cboId = localStorage.getItem('active_cbo_id');
    return {
      'Authorization': `Bearer ${token}`,
      'X-CBO-ID': cboId || '',
      'Content-Type': 'application/json',
    };
  }, []);

  // Load initial data
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const headers = getHeaders();
        const [docsRes, onboardingRes, sandboxRes] = await Promise.all([
          fetch(`${apiUrl}/api/developer-portal/docs`, { headers }),
          fetch(`${apiUrl}/api/developer-portal/onboarding`, { headers }),
          fetch(`${apiUrl}/api/developer-portal/sandbox`, { headers }),
        ]);

        if (docsRes.ok) {
          const docs = await docsRes.json();
          setDocCategories(docs);
        }
        if (onboardingRes.ok) {
          const ob = await onboardingRes.json();
          setOnboarding(ob);
        }
        if (sandboxRes.ok) {
          const sb = await sandboxRes.json();
          setSandbox(sb);
        }
      } catch (err) {
        console.error('Failed to load developer portal data:', err);
        setError('Failed to load developer portal. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [apiUrl, getHeaders]);

  // Load usage stats when sandbox is available
  useEffect(() => {
    if (sandbox?.id && activeTab === 'usage') {
      const loadUsage = async () => {
        try {
          const res = await fetch(`${apiUrl}/api/developer-portal/sandbox/${sandbox.id}/usage`, {
            headers: getHeaders(),
          });
          if (res.ok) setUsage(await res.json());
        } catch (err) {
          console.error('Failed to load usage stats:', err);
        }
      };
      loadUsage();
    }
  }, [sandbox, activeTab, apiUrl, getHeaders]);

  // Load curated workflows when the API Guide tab is first opened
  useEffect(() => {
    if (activeTab !== 'api-guide' || workflowsLoaded) return;
    const loadWorkflows = async () => {
      setWorkflowsLoading(true);
      try {
        const res = await fetch(`${apiUrl}/api/api-guide/workflows`, { headers: getHeaders() });
        if (res.ok) {
          const data = await res.json();
          setWorkflows(Array.isArray(data.workflows) ? data.workflows : []);
        }
        setWorkflowsLoaded(true);
      } catch (err) {
        console.error('Failed to load API guide workflows:', err);
        setError('Failed to load the API guide. Please try again.');
      } finally {
        setWorkflowsLoading(false);
      }
    };
    loadWorkflows();
  }, [activeTab, workflowsLoaded, apiUrl, getHeaders]);

  // Entitlement drives which collections are offered. A failure here is not
  // worth an error banner: the page still works, the doula collection simply
  // falls back to the key-based check above.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${apiUrl}/api/developer-portal/scopes`, { headers: getHeaders() });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled && Array.isArray(data?.scopes)) setGrantableScopes(data.scopes);
      } catch {
        /* leave it empty; the sandbox-based fallback still applies */
      }
    })();
    return () => { cancelled = true; };
  }, [apiUrl, getHeaders]);

  const requestSandbox = async () => {
    if (!useCase || useCase.length < 10) {
      setError('Please describe your use case (at least 10 characters)');
      return;
    }
    setSandboxRequesting(true);
    setError(null);
    try {
      const res = await fetch(`${apiUrl}/api/developer-portal/sandbox/request`, {
        method: 'POST',
        headers: getHeaders(),
        // requested_scopes is deliberately omitted. It used to be hard-coded to
        // three read-only scopes, so every key provisioned here came back unable
        // to reach the partner surface — an Ohai key could not call the doula
        // API it exists for. The server now grants whatever the organisation is
        // entitled to, derived from the capabilities it actually runs, which is
        // the only place that knows the answer.
        body: JSON.stringify({
          use_case: useCase,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        setSandbox(data);
        setNewApiKey(data.api_key);
        // Refresh onboarding
        const obRes = await fetch(`${apiUrl}/api/developer-portal/onboarding`, { headers: getHeaders() });
        if (obRes.ok) setOnboarding(await obRes.json());
      } else {
        const err = await res.json();
        setError(err.detail || 'Failed to provision sandbox');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setSandboxRequesting(false);
    }
  };

  const rotateApiKey = async () => {
    if (!sandbox) return;
    if (!confirm('This will immediately invalidate your current API key. Continue?')) return;
    try {
      const res = await fetch(`${apiUrl}/api/developer-portal/sandbox/${sandbox.id}/rotate-key`, {
        method: 'POST',
        headers: getHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        setNewApiKey(data.api_key);
        setActiveTab('sandbox');  // Ensure we're on the sandbox tab to see the key
        // Also send to clipboard and notify
        if (data.api_key) {
          try { await navigator.clipboard.writeText(data.api_key); } catch {}
          alert(`API key rotated! The new key has been copied to your clipboard.\n\nKey: ${data.api_key}\n\nSave this key — it will not be shown again. A confirmation email has been sent.`);
        }
      } else {
        const err = await res.json();
        setError(err.detail || 'Failed to rotate API key');
      }
    } catch (err) {
      setError('Failed to rotate API key');
    }
  };

  const deactivateSandbox = async () => {
    if (!sandbox) return;
    // Typed confirmation rather than a yes/no. Revoking a key breaks every
    // integration using it, and the usual confirm() is dismissed reflexively.
    const typed = prompt(
      'Deactivating revokes this key immediately — any integration using it will start failing.\n\n' +
      'The key cannot be reactivated here; you would request a new sandbox, which issues a different key.\n\n' +
      'Type DEACTIVATE to confirm:'
    );
    if (typed !== 'DEACTIVATE') return;
    setDeactivating(true);
    setError(null);
    try {
      const res = await fetch(`${apiUrl}/api/developer-portal/sandbox/${sandbox.id}/deactivate`, {
        method: 'POST',
        headers: getHeaders(),
      });
      if (res.ok) {
        // Clear local state so the page offers provisioning again rather than
        // showing a panel for a key that no longer works.
        setSandbox(null);
        setNewApiKey(null);
      } else {
        const err = await res.json().catch(() => ({}));
        setError(err.detail || 'Failed to deactivate the sandbox');
      }
    } catch (err) {
      setError('Could not reach the API. The key may or may not have been deactivated — reload to check.');
    } finally {
      setDeactivating(false);
    }
  };

  // Filter docs by search
  const filteredDocs = searchQuery
    ? docCategories.map(cat => ({
        ...cat,
        docs: cat.docs.filter(d =>
          d.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          (d.excerpt || '').toLowerCase().includes(searchQuery.toLowerCase())
        ),
      })).filter(cat => cat.docs.length > 0)
    : docCategories;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-6 py-6">
          {/* Tabs */}
          <div className="flex gap-1">
            {[
              { key: 'overview', label: 'Overview', icon: <Rocket className="w-4 h-4" /> },
              { key: 'docs', label: 'Documentation', icon: <Book className="w-4 h-4" /> },
              { key: 'api-guide', label: 'API Guide', icon: <Code className="w-4 h-4" /> },
              { key: 'sandbox', label: 'Sandbox & API Keys', icon: <Key className="w-4 h-4" /> },
              { key: 'usage', label: 'Usage & Analytics', icon: <BarChart3 className="w-4 h-4" /> },
            ].map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key as typeof activeTab)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activeTab === tab.key
                    ? 'bg-blue-50 text-blue-700'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Error Banner */}
      {error && (
        <div className="max-w-7xl mx-auto px-6 mt-4">
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-red-500" />
            <span className="text-sm text-red-700">{error}</span>
            <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-600">&times;</button>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* ============================================================ */}
        {/* OVERVIEW TAB */}
        {/* ============================================================ */}
        {activeTab === 'overview' && (
          <GatedTabContent pageKey="developer-portal" tabKey="overview"><div className="space-y-8">
            {/* Onboarding Progress */}
            <div className="bg-white rounded-xl border p-6">
              <h2 className="text-lg font-semibold mb-4">Onboarding Progress</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  { key: 'account_created', label: '1. Account Created', number: 1 },
                  { key: 'sandbox_provisioned', label: '2. Sandbox Provisioned', number: 2 },
                  { key: 'first_api_call', label: '3. First API Call', number: 3 },
                  { key: 'webhook_configured', label: '4. Webhook Configured', number: 4 },
                  { key: 'frontend_accessed', label: '5. Frontend Explored', number: 5 },
                  { key: 'production_requested', label: '6. Production Requested', number: 6 },
                  { key: 'production_approved', label: '7. Production Approved', number: 7 },
                ].map(step => {
                  const stepData = onboarding?.steps?.[step.key];
                  const completed = typeof stepData === 'boolean' ? stepData : stepData?.completed || false;
                  const timestamp = typeof stepData === 'object' ? stepData?.at : undefined;
                  return (
                    <StepIndicator
                      key={step.key}
                      completed={completed}
                      label={step.label}
                      timestamp={timestamp}
                      number={step.number}
                    />
                  );
                })}
              </div>
            </div>

            {/* Two Services */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl border p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                    <Server className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold">WeaveIn Platform API</h3>
                    <p className="text-xs text-gray-500">Primary API — 165+ endpoints</p>
                  </div>
                </div>
                <p className="text-sm text-gray-600 mb-4">
                  Case management, workforce management, marketing &amp; sales, care coordination,
                  member engagement, billing, communications, AI intelligence, and more.
                </p>
                <div className="flex flex-wrap gap-2">
                  {['Members', 'Referrals', 'Workflows', 'Billing', 'AI', 'Communications', 'Analytics'].map(tag => (
                    <span key={tag} className="px-2 py-1 bg-indigo-50 text-indigo-700 text-xs rounded-full">{tag}</span>
                  ))}
                </div>
                <button
                  onClick={() => { setActiveTab('docs'); setSelectedDoc(docCategories.flatMap(c => c.docs).find(d => d.slug === 'api-weavein-platform') || null); }}
                  className="mt-4 flex items-center gap-1 text-sm text-indigo-600 hover:text-indigo-800 font-medium"
                >
                  View API Reference <ArrowRight className="w-4 h-4" />
                </button>
              </div>

              <div className="bg-white rounded-xl border p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                    <Database className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold">Provider Directory API</h3>
                    <p className="text-xs text-gray-500">1M+ providers &amp; CBOs</p>
                  </div>
                </div>
                <p className="text-sm text-gray-600 mb-4">
                  Geocoded provider and CBO directory sourced from HRSA, SAMHSA, CMS, and 211.
                  Radius search, ZIP-based search, advanced filtering, and ETL management.
                </p>
                <div className="flex flex-wrap gap-2">
                  {['Radius Search', 'ZIP Search', 'Geocoding', 'HRSA', 'SAMHSA', 'CMS', '211'].map(tag => (
                    <span key={tag} className="px-2 py-1 bg-emerald-50 text-emerald-700 text-xs rounded-full">{tag}</span>
                  ))}
                </div>
                <button
                  onClick={() => { setActiveTab('docs'); setSelectedDoc(docCategories.flatMap(c => c.docs).find(d => d.slug === 'api-provider-directory') || null); }}
                  className="mt-4 flex items-center gap-1 text-sm text-emerald-600 hover:text-emerald-800 font-medium"
                >
                  View API Reference <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Quick Links */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <button
                onClick={() => window.open(`${apiUrl}/docs`, '_blank')}
                className="bg-white rounded-xl border p-5 text-left hover:border-blue-300 transition-colors group"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Zap className="w-5 h-5 text-amber-500" />
                    <div>
                      <p className="font-medium">Interactive Swagger UI</p>
                      <p className="text-xs text-gray-500">Try API calls directly in the browser</p>
                    </div>
                  </div>
                  <ExternalLink className="w-4 h-4 text-gray-400 group-hover:text-blue-500" />
                </div>
              </button>

              <button
                onClick={() => setActiveTab('sandbox')}
                className="bg-white rounded-xl border p-5 text-left hover:border-blue-300 transition-colors group"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Key className="w-5 h-5 text-blue-500" />
                    <div>
                      <p className="font-medium">Get Sandbox API Keys</p>
                      <p className="text-xs text-gray-500">Provision your sandbox in seconds</p>
                    </div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-blue-500" />
                </div>
              </button>

              <button
                onClick={() => { setActiveTab('docs'); setSelectedDoc(docCategories.flatMap(c => c.docs).find(d => d.slug === 'webhooks') || null); }}
                className="bg-white rounded-xl border p-5 text-left hover:border-blue-300 transition-colors group"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Webhook className="w-5 h-5 text-purple-500" />
                    <div>
                      <p className="font-medium">Webhooks & Events</p>
                      <p className="text-xs text-gray-500">Real-time event notifications</p>
                    </div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-blue-500" />
                </div>
              </button>
            </div>

            {/* Platform Capabilities Summary */}
            <div className="bg-white rounded-xl border p-6">
              <h2 className="text-lg font-semibold mb-4">Platform Capabilities</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { icon: <Users className="w-5 h-5" />, label: 'Member Management', desc: 'CRUD, enrichment, retention' },
                  { icon: <FileText className="w-5 h-5" />, label: 'Case Management', desc: 'Referrals, notes, episodes' },
                  { icon: <Globe className="w-5 h-5" />, label: 'Care Coordination', desc: 'Workflows, enrollments, telehealth' },
                  { icon: <Users className="w-5 h-5" />, label: 'Workforce', desc: 'Teams, tasks, goals, calendar' },
                  { icon: <MessageSquare className="w-5 h-5" />, label: 'Communications', desc: 'SMS, email, WhatsApp, voice' },
                  { icon: <BarChart3 className="w-5 h-5" />, label: 'Analytics & AI', desc: 'ML predictions, RAG, dashboards' },
                  { icon: <Shield className="w-5 h-5" />, label: 'Compliance', desc: 'HIPAA, consent, 2FA, audit' },
                  { icon: <Database className="w-5 h-5" />, label: 'Billing', desc: 'Claims, invoices, Stripe, ROI' },
                ].map(cap => (
                  <div key={cap.label} className="p-3 rounded-lg bg-gray-50">
                    <div className="text-blue-600 mb-2">{cap.icon}</div>
                    <p className="font-medium text-sm">{cap.label}</p>
                    <p className="text-xs text-gray-500">{cap.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div></GatedTabContent>
        )}

        {/* ============================================================ */}
        {/* DOCUMENTATION TAB */}
        {/* ============================================================ */}
        {activeTab === 'docs' && (
          <GatedTabContent pageKey="developer-portal" tabKey="docs"><div className="flex gap-6">
            {/* Sidebar */}
            <div className="w-72 flex-shrink-0">
              <div className="sticky top-6 space-y-4">
                {/* Search */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search docs..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* Doc Navigation */}
                <nav className="bg-white rounded-xl border p-4 space-y-4">
                  {filteredDocs.map(cat => (
                    <div key={cat.category}>
                      <div className="flex items-center gap-2 text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                        {categoryIcons[cat.category] || <FileText className="w-4 h-4" />}
                        {categoryLabels[cat.category] || cat.category}
                      </div>
                      <div className="space-y-1">
                        {cat.docs.map(doc => (
                          <button
                            key={doc.slug}
                            onClick={() => setSelectedDoc(doc)}
                            className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                              selectedDoc?.slug === doc.slug
                                ? 'bg-blue-50 text-blue-700 font-medium'
                                : 'text-gray-600 hover:bg-gray-50'
                            }`}
                          >
                            {doc.title}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}

                  {/* Swagger Link */}
                  <div className="pt-3 border-t">
                    <a
                      href={`${apiUrl}/docs`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-lg"
                    >
                      <ExternalLink className="w-4 h-4" />
                      Interactive Swagger UI
                    </a>
                    <a
                      href={`${apiUrl}/redoc`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-lg"
                    >
                      <ExternalLink className="w-4 h-4" />
                      ReDoc API Reference
                    </a>
                  </div>
                </nav>
              </div>
            </div>

            {/* Content Area */}
            <div className="flex-1 min-w-0">
              {selectedDoc ? (
                <div className="bg-white rounded-xl border p-8">
                  <div className="flex items-center gap-2 text-xs text-gray-500 mb-4">
                    <span>{categoryLabels[selectedDoc.category] || selectedDoc.category}</span>
                    {selectedDoc.subcategory && (
                      <>
                        <ChevronRight className="w-3 h-3" />
                        <span>{selectedDoc.subcategory}</span>
                      </>
                    )}
                  </div>
                  <div className="prose prose-sm max-w-none">
                    {/* Render markdown content as HTML-safe blocks */}
                    {selectedDoc.content.split('\n').map((line, i) => {
                      if (line.startsWith('# ')) return <h1 key={i} className="text-2xl font-bold mt-6 mb-3">{line.slice(2)}</h1>;
                      if (line.startsWith('## ')) return <h2 key={i} className="text-xl font-semibold mt-5 mb-2">{line.slice(3)}</h2>;
                      if (line.startsWith('### ')) return <h3 key={i} className="text-lg font-medium mt-4 mb-2">{line.slice(4)}</h3>;
                      if (line.startsWith('```')) return null; // code blocks handled separately
                      if (line.startsWith('| ')) {
                        return <p key={i} className="font-mono text-xs bg-gray-50 px-2 py-1">{line}</p>;
                      }
                      if (line.startsWith('- ')) return <li key={i} className="ml-4 text-gray-700">{line.slice(2)}</li>;
                      if (line.startsWith('> ')) return <blockquote key={i} className="border-l-4 border-blue-300 pl-4 text-gray-600 italic">{line.slice(2)}</blockquote>;
                      if (line.trim() === '') return <br key={i} />;
                      return <p key={i} className="text-gray-700 leading-relaxed">{line}</p>;
                    })}
                  </div>
                  {selectedDoc.updated_at && (
                    <p className="text-xs text-gray-400 mt-8 border-t pt-4">
                      Last updated: {new Date(selectedDoc.updated_at).toLocaleDateString()}
                    </p>
                  )}
                </div>
              ) : (
                <div className="bg-white rounded-xl border p-8 text-center text-gray-500">
                  <Book className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p className="font-medium">Select a documentation page</p>
                  <p className="text-sm mt-1">Choose a topic from the sidebar to get started</p>
                </div>
              )}
            </div>
          </div></GatedTabContent>
        )}

        {/* ============================================================ */}
        {/* API GUIDE TAB */}
        {/* ============================================================ */}
        {activeTab === 'api-guide' && (
          <GatedTabContent pageKey="developer-portal" tabKey="api-guide"><div className="space-y-6">
            {/* Intro + downloads */}
            <div className="bg-white rounded-xl border p-6">
              <div className="flex items-start justify-between gap-6 flex-wrap">
                <div className="max-w-2xl">
                  <h2 className="text-lg font-semibold text-ink">Curated API Workflows</h2>
                  <p className="text-sm text-gray-600 mt-1">
                    Ordered endpoint sequences for the most common integration paths. These are served
                    live from the API, so they always reflect the current platform.
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <a
                    href="/weavein-api.postman_collection.json"
                    download
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
                  >
                    <Code className="w-4 h-4" />
                    Platform Collection
                  </a>
                  {hasDoulaEntitlement && (
                    <a
                      href="/weavein-partner-doula.postman_collection.json"
                      download
                      className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
                    >
                      <Code className="w-4 h-4" />
                      Partner Doula Collection
                    </a>
                  )}
                  {hasDoulaEntitlement && (
                    <a
                      href={`${apiUrl}/api/developer-portal/reference/partner`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-4 py-2 border rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
                    >
                      <ExternalLink className="w-4 h-4" />
                      Partner API Reference
                    </a>
                  )}
                  {/* The platform /docs is 8.3MB over 4,695 paths — Swagger UI
                      parses and renders all of it client-side, so the tab stalls
                      rather than erroring. Kept, because it is the only view of
                      the whole surface, but no longer the first thing offered. */}
                  <a
                    href={`${apiUrl}/docs`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-4 py-2 border rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
                    title="The full platform spec — large, and slow to render"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Full Platform Spec
                  </a>
                </div>
              </div>
              <div className="mt-5 pt-4 border-t">
                <p className="text-xs font-medium text-gray-700 mb-2">Every partner file</p>
                <ul className="text-xs text-gray-600 space-y-1">
                  <li>
                    <a href="/weavein-api.postman_collection.json" download className="text-blue-600 hover:underline">
                      weavein-api.postman_collection.json
                    </a>
                    {' '}— the platform API, 34 requests in 8 folders
                  </li>
                  {hasDoulaEntitlement && (
                    <li>
                      <a href="/weavein-partner-doula.postman_collection.json" download className="text-blue-600 hover:underline">
                        weavein-partner-doula.postman_collection.json
                      </a>
                      {' '}— the Partner Doula API, 18 requests in 7 folders
                    </li>
                  )}
                  <li>
                    <a href="/weavein-sandbox.postman_environment.json" download className="text-blue-600 hover:underline">
                      weavein-sandbox.postman_environment.json
                    </a>
                    {' '}— environment, sandbox
                  </li>
                  <li>
                    <a href="/weavein-production.postman_environment.json" download className="text-blue-600 hover:underline">
                      weavein-production.postman_environment.json
                    </a>
                    {' '}— environment, production
                  </li>
                </ul>
                <p className="text-xs text-gray-500 mt-3">
                  Import a collection together with one environment, then set{' '}
                  <code className="px-1 bg-gray-100 rounded">api_key</code> and{' '}
                  <code className="px-1 bg-gray-100 rounded">org_id</code>. The key travels in the{' '}
                  <code className="px-1 bg-gray-100 rounded">X-API-Key</code> header, which the collections
                  already set for you.
                </p>
                <p className="text-xs text-gray-500 mt-2">
                  To confirm a key works, send an <em>authenticated</em> request and read the status:{' '}
                  <code className="px-1 bg-gray-100 rounded">401</code> the key is not valid,{' '}
                  <code className="px-1 bg-gray-100 rounded">403</code> valid but lacking the scope,{' '}
                  <code className="px-1 bg-gray-100 rounded">200</code> or{' '}
                  <code className="px-1 bg-gray-100 rounded">404</code> valid and authorised. Health
                  endpoints are public and answer <code className="px-1 bg-gray-100 rounded">200</code>{' '}
                  to any key, valid or not, so they cannot confirm one.
                </p>
              </div>
            </div>

            {workflowsLoading && (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
              </div>
            )}

            {!workflowsLoading && workflows.length === 0 && workflowsLoaded && (
              <div className="bg-white rounded-xl border p-8 text-center text-gray-500">
                <Code className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p className="font-medium">No workflows available</p>
                <p className="text-sm mt-1">The API guide could not be loaded for this organization.</p>
              </div>
            )}

            {!workflowsLoading && workflows.map((wf, i) => (
              <div key={wf.title || i} className="bg-white rounded-xl border overflow-hidden">
                <div className="px-6 py-4 border-b bg-gray-50">
                  <h3 className="font-semibold text-ink">{wf.title}</h3>
                  <p className="text-sm text-gray-600 mt-0.5">{wf.description}</p>
                </div>
                <div className="divide-y">
                  {(wf.steps || []).map(step => (
                    <div key={step.order} className="px-6 py-4 flex items-start gap-4">
                      <div className="flex-shrink-0 w-7 h-7 rounded-full bg-blue-50 text-blue-700 text-xs font-semibold flex items-center justify-center mt-0.5">
                        {step.order}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`px-2 py-0.5 rounded text-xs font-semibold ${methodColors[step.method] || 'bg-gray-100 text-gray-700'}`}>
                            {step.method}
                          </span>
                          <code className="text-sm text-gray-800 break-all">{step.path}</code>
                          <button
                            onClick={() => navigator.clipboard.writeText(`${apiUrl}${step.path}`)}
                            title="Copy full URL"
                            className="text-gray-400 hover:text-gray-600"
                          >
                            <Copy className="w-3.5 h-3.5" />
                          </button>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">{step.description}</p>
                        {step.required_fields && step.required_fields.length > 0 && (
                          <p className="text-xs text-gray-500 mt-1">
                            Required: {step.required_fields.map(f => (
                              <code key={f} className="px-1 mx-0.5 bg-gray-100 rounded">{f}</code>
                            ))}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div></GatedTabContent>
        )}

        {/* ============================================================ */}
        {/* SANDBOX TAB */}
        {/* ============================================================ */}
        {activeTab === 'sandbox' && (
          <GatedTabContent pageKey="developer-portal" tabKey="sandbox"><div className="space-y-6">
            {/* New API Key Alert */}
            {newApiKey && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                  <div className="flex-1">
                    <p className="font-semibold text-amber-800">Save Your API Key Now</p>
                    <p className="text-sm text-amber-700 mt-1">
                      This key will only be shown once. Copy it and store it securely.
                    </p>
                    <div className="mt-3 flex items-center gap-3">
                      <code className="bg-amber-100 px-3 py-2 rounded text-sm font-mono flex-1 break-all">
                        {newApiKey}
                      </code>
                      <CopyButton text={newApiKey} label="Copy Key" />
                    </div>
                    <button
                      onClick={() => setNewApiKey(null)}
                      className="mt-3 text-sm text-amber-600 hover:text-amber-800 font-medium"
                    >
                      I&apos;ve saved it — dismiss
                    </button>
                  </div>
                </div>
              </div>
            )}

            {sandbox ? (
              /* Sandbox exists — show details */
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Sandbox Details */}
                <div className="lg:col-span-2 bg-white rounded-xl border p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-lg font-semibold">Sandbox Environment</h2>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      sandbox.status === 'active' ? 'bg-green-100 text-green-700' :
                      sandbox.status === 'suspended' ? 'bg-red-100 text-red-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {sandbox.status}
                    </span>
                  </div>

                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs text-gray-500">Client ID</label>
                        <div className="flex items-center gap-2 mt-1">
                          <code className="text-sm font-mono bg-gray-50 px-2 py-1 rounded">{sandbox.client_id}</code>
                          <CopyButton text={sandbox.client_id || ''} />
                        </div>
                      </div>
                      <div>
                        <label className="text-xs text-gray-500">API Key</label>
                        <div className="flex items-center gap-2 mt-1">
                          <code className="text-sm font-mono bg-gray-50 px-2 py-1 rounded">
                            {showApiKey ? sandbox.api_key_prefix + '...' : '••••••••••••'}
                          </code>
                          <button onClick={() => setShowApiKey(!showApiKey)} className="text-gray-400 hover:text-gray-600">
                            {showApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                          <button onClick={rotateApiKey} className="text-gray-400 hover:text-gray-600" title="Rotate key">
                            <RefreshCw className="w-4 h-4" />
                          </button>
                          <button
                            onClick={deactivateSandbox}
                            disabled={deactivating}
                            className="text-gray-400 hover:text-red-600 disabled:opacity-50"
                            title="Deactivate key — revokes it immediately"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-4 pt-4 border-t">
                      <div>
                        <label className="text-xs text-gray-500">Rate Limit</label>
                        <p className="font-medium">{sandbox.rate_limit_per_minute} req/min</p>
                      </div>
                      <div>
                        <label className="text-xs text-gray-500">Daily Quota</label>
                        <p className="font-medium">{sandbox.daily_request_quota?.toLocaleString()}</p>
                      </div>
                      <div>
                        <label className="text-xs text-gray-500">Monthly Quota</label>
                        <p className="font-medium">{sandbox.monthly_request_quota?.toLocaleString()}</p>
                      </div>
                    </div>

                    <div className="pt-4 border-t">
                      <label className="text-xs text-gray-500">Allowed Scopes</label>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {sandbox.allowed_scopes?.map(scope => (
                          <span key={scope} className="px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded-full">{scope}</span>
                        ))}
                      </div>
                    </div>

                    {sandbox.webhook_url && (
                      <div className="pt-4 border-t">
                        <label className="text-xs text-gray-500">Webhook URL</label>
                        <p className="text-sm font-mono mt-1">{sandbox.webhook_url}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Quick Start Code */}
                <div className="bg-white rounded-xl border p-6">
                  <h3 className="font-semibold mb-4">Quick Start</h3>
                  <div className="space-y-4">
                    <div>
                      <p className="text-xs text-gray-500 mb-2">1. Authenticate</p>
                      <CodeBlock code={`curl -X POST ${apiUrl}/api/auth/login \\
  -H "Content-Type: application/json" \\
  -d '{"email": "you@org.com", "password": "***"}'`} />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-2">2. List Members</p>
                      <CodeBlock code={`curl -X GET ${apiUrl}/api/members \\
  -H "Authorization: Bearer TOKEN" \\
  -H "X-CBO-ID: ${sandbox.organization_id}"`} />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-2">3. Search the Provider Directory</p>
                      <CodeBlock code={`curl -X GET "https://provider-directory-api.onrender.com/api/providers?state=CA&limit=10"`} />
                    </div>
                  </div>
                  <div className="mt-5 pt-4 border-t text-sm text-gray-600">
                    <p className="font-medium text-gray-800 mb-2">Browse the full live API</p>
                    <div className="flex flex-wrap gap-3">
                      <a
                        href={`${apiUrl}/docs`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100"
                      >
                        <ExternalLink className="w-4 h-4" />
                        Swagger UI
                      </a>
                      <a
                        href={`${apiUrl}/redoc`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100"
                      >
                        <ExternalLink className="w-4 h-4" />
                        ReDoc
                      </a>
                      <a
                        href={`${apiUrl}/openapi.json`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100"
                      >
                        <ExternalLink className="w-4 h-4" />
                        OpenAPI Spec (JSON)
                      </a>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      The OpenAPI spec is auto-generated from live route registrations — always current with what&apos;s deployed.
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              /* No sandbox — show provisioning form */
              <div className="max-w-2xl mx-auto">
                <div className="bg-white rounded-xl border p-8 text-center">
                  <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Key className="w-8 h-8 text-blue-600" />
                  </div>
                  <h2 className="text-xl font-bold mb-2">Get Sandbox API Access</h2>
                  <p className="text-gray-500 mb-6">
                    Provision a fully functional sandbox environment with sample data,
                    API credentials, and access to all platform features.
                  </p>

                  <div className="text-left space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Describe your integration use case *
                      </label>
                      <textarea
                        value={useCase}
                        onChange={(e) => setUseCase(e.target.value)}
                        placeholder="e.g., We want to integrate member referral data with our EHR system..."
                        className="w-full border rounded-lg p-3 text-sm h-24 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-sm font-medium mb-2">What you&apos;ll get:</p>
                      <ul className="space-y-2 text-sm text-gray-600">
                        <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> Sandbox API key &amp; client ID</li>
                        <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> 60 requests/minute, 10K daily quota</li>
                        <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> Access to all API endpoints</li>
                        <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> Sample data pre-loaded</li>
                        <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> Interactive Swagger docs</li>
                        <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> Frontend platform access</li>
                      </ul>
                    </div>

                    <button
                      onClick={requestSandbox}
                      disabled={sandboxRequesting}
                      className="w-full bg-blue-600 text-white py-3 rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {sandboxRequesting ? (
                        <><Loader2 className="w-4 h-4 animate-spin" /> Provisioning...</>
                      ) : (
                        <><Rocket className="w-4 h-4" /> Provision Sandbox</>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div></GatedTabContent>
        )}

        {/* ============================================================ */}
        {/* USAGE TAB */}
        {/* ============================================================ */}
        {activeTab === 'usage' && (
          <GatedTabContent pageKey="developer-portal" tabKey="usage"><div className="space-y-6">
            {usage ? (
              <>
                {/* Stats Grid */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  {[
                    { label: 'Total Requests', value: usage.total_requests.toLocaleString() },
                    { label: 'Today', value: usage.requests_today.toLocaleString() },
                    { label: 'This Month', value: usage.requests_this_month.toLocaleString() },
                    { label: 'Errors', value: usage.error_count.toLocaleString() },
                    { label: 'Avg Response', value: usage.avg_response_time_ms ? `${usage.avg_response_time_ms}ms` : 'N/A' },
                  ].map(stat => (
                    <div key={stat.label} className="bg-white rounded-xl border p-4">
                      <p className="text-xs text-gray-500">{stat.label}</p>
                      <p className="text-2xl font-bold mt-1">{stat.value}</p>
                    </div>
                  ))}
                </div>

                {/* Top Endpoints */}
                <div className="bg-white rounded-xl border p-6">
                  <h3 className="font-semibold mb-4">Top Endpoints</h3>
                  {usage.top_endpoints.length > 0 ? (
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-gray-500 border-b">
                          <th className="pb-2">Method</th>
                          <th className="pb-2">Endpoint</th>
                          <th className="pb-2 text-right">Requests</th>
                        </tr>
                      </thead>
                      <tbody>
                        {usage.top_endpoints.map((ep, i) => (
                          <tr key={i} className="border-b last:border-0">
                            <td className="py-2">
                              <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                ep.method === 'GET' ? 'bg-green-100 text-green-700' :
                                ep.method === 'POST' ? 'bg-blue-100 text-blue-700' :
                                'bg-gray-100 text-gray-700'
                              }`}>{ep.method}</span>
                            </td>
                            <td className="py-2 font-mono text-xs">{ep.endpoint}</td>
                            <td className="py-2 text-right">{ep.count.toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <p className="text-gray-500 text-sm">No API calls recorded yet. Make your first request to see usage data.</p>
                  )}
                </div>
              </>
            ) : sandbox ? (
              <div className="bg-white rounded-xl border p-8 text-center text-gray-500">
                <BarChart3 className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p className="font-medium">No usage data yet</p>
                <p className="text-sm mt-1">Make your first API call to start tracking usage</p>
              </div>
            ) : (
              <div className="bg-white rounded-xl border p-8 text-center text-gray-500">
                <Key className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p className="font-medium">Sandbox not provisioned</p>
                <p className="text-sm mt-1">
                  <button onClick={() => setActiveTab('sandbox')} className="text-blue-600 hover:underline">
                    Provision your sandbox
                  </button>
                  {' '}to start tracking API usage
                </p>
              </div>
            )}
          </div></GatedTabContent>
        )}
      </div>
    </div>
  );
}
